import { Component, Directive, OnInit, Input, Output, EventEmitter } from "@angular/core";
import { animeDialog, animeBackground, animeSubmodal, animeImgShow } from "../../animations";
import { finalize } from "rxjs/operators";
import { lastValueFrom } from "rxjs";
import moment from "moment-timezone";

import { Modal } from "../../../model/modal";
import { UsersService } from "../../../services/users.service";
import { DealerService } from "../../../services/dealer.service";
import { PaymentService } from "../../../services/payment.service";
import { DealerPlanService } from "../../../services/service-plan.service";
import { Helper } from "../../../../4services/2helper";
import { BaseComponent } from "@app/directives/base-component";

type PaymentMethodType = "Credit or Debit card" | "ACH Bank Accounts" | "SEPA" | "Others(Check By Mail, etc.)";

@Component({
  selector: "setting-plan-payment",
  templateUrl: "./plan-payment.component.html",
  styleUrls: ["../../common.scss", "./plan-payment.component.scss"],
  animations: [animeDialog, animeBackground, animeSubmodal, animeImgShow],
  standalone: false,
})
export class PlanPaymentComponent extends BaseComponent implements OnInit {
  @Input() active: boolean;
  @Input() modal: Modal;
  @Output() onClose: EventEmitter<any> = new EventEmitter();
  @Output() onUpdate: EventEmitter<any> = new EventEmitter();

  // ───────────── User signals ─────────────
  dealerId = this.usersService.dealerId;
  dealerType = this.usersService.dealerType;
  me = this.usersService.me;

  // ───────────── Plan ─────────────
  currentPlanType = 1;
  selectedPlan = 1;
  selectedPlanTitle = "Basic";
  selectedPlanFee: string;

  // ───────────── Customer ─────────────
  isNewDealer = false;
  hasStripeCustomerAccount = false;
  companyName = "";
  firstname = "";
  lastname = "";
  customerEmail = "";

  // ───────────── Payment methods ─────────────
  cardAccounts = [];
  bankAccounts = [];
  sepaAccounts = [];

  paymentMethod: PaymentMethodType | string = "Credit or Debit card";
  paymentInfo: any = {};
  isValidPaymentInfo = false;

  // ───────────── UI state ─────────────
  isLoading = false;
  m_warning = new Modal();
  m_payment_information = new Modal();

  constructor(
    private usersService: UsersService,
    private dealerService: DealerService,
    private paymentService: PaymentService,
    public dealerPlanService: DealerPlanService,
    private helper: Helper,
  ) {
    super();
  }

  ngOnInit() {
    this.isLoading = true;
    this.parseSelectedPlan();
    this.checkIsNewDealer();
    this.fetchData();
    this.setCustomerData();
  }

  closeModal(res = null) {
    this.onClose.next(res);
    this.modal.close();
  }

  // ─────────────────────────────────────────────
  // Plan
  // ─────────────────────────────────────────────
  parseSelectedPlan() {
    this.selectedPlan = this.modal.data;
    this.selectedPlanTitle = this.dealerPlanService.planDescription[this.modal.data].title;
    this.selectedPlanFee = this.formatPlanFee(this.selectedPlan);
  }

  formatPlanFee(planId: number): string {
    const symbol = this.me()?.currency_symbol;
    switch (planId) {
      case 2:
        return `${symbol} ${this.dealerPlanService.dealerPlanPriceProfessional}`;
      case 3:
        return `${symbol} ${this.dealerPlanService.dealerPlanPricePremium}`;
      case 4:
        return `${symbol} ${this.dealerPlanService.mainDealerPlanPriceReseller}`;
      case 1:
      default:
        return "per site fee";
    }
  }

  getCurrentDealerPlan() {
    this.dealerPlanService.getDealerPlan(this.dealerId()).subscribe((plan) => {
      this.currentPlanType = plan?.current_month_service_plan_type ?? 1;
    });
  }

  updateDealerPlan(plan) {
    const dealerId = this.dealerId();
    const data = { service_plan_type: plan };
    this.dealerPlanService.updateDealerPlan(dealerId, data).subscribe(
      () => {
        this.closeModal(this.selectedPlan);
        this.isLoading = false;
      },
      (err) => {
        this.errorDialog(err, "Set Dealer Plan");
        this.isLoading = false;
      },
    );
  }

  // ─────────────────────────────────────────────
  // Customer
  // ─────────────────────────────────────────────
  checkIsNewDealer() {
    const created = moment.utc(this.me()?.created);
    const diffDays = moment.utc().diff(created, "days");
    this.isNewDealer = diffDays <= 30;
  }

  setCustomerData() {
    this.dealerService.getDealerInfo(this.dealerId()).subscribe(
      (res) => {
        const me = this.me();
        this.companyName = res[0].company_name;
        this.firstname = me?.firstname;
        this.lastname = me?.lastname;
        this.customerEmail = me?.email;
        this.isLoading = false;
      },
      (err) => {
        console.log(err);
        this.isLoading = false;
      },
    );
  }

  // ─────────────────────────────────────────────
  // Fetch payment methods
  // ─────────────────────────────────────────────
  async fetchData() {
    const dealerId = this.dealerId();
    const dealerType = this.dealerType();
    await Promise.all([
      this.fetchPlanPricing(dealerId, dealerType),
      this.fetchCardAccounts(dealerId),
      this.fetchACHAccounts(dealerId),
      this.fetchSEPAAccounts(dealerId),
    ]);
    this.getCurrentDealerPlan();
  }

  async fetchPlanPricing(dealerId, dealerType) {
    await lastValueFrom(this.dealerPlanService.getDealerServicePlanPricing(dealerId, dealerType));
  }

  async fetchCardAccounts(dealerId) {
    try {
      const customers = await lastValueFrom(this.paymentService.getStripe(dealerId));
      this.hasStripeCustomerAccount = customers.length > 0;
      this.cardAccounts = this.extractCardsFromCustomers(customers);
    } catch (err) {
      this.hasStripeCustomerAccount = false;
      this.cardAccounts = [];
      this.isLoading = false;
    }
  }

  extractCardsFromCustomers(customers): any[] {
    if (!customers.length) return [];
    if (customers[0]["meta"].deleted) return [];
    const sources = customers[0].meta.sources.data ?? [];
    return sources.filter((data) => data.object === "card");
  }

  async fetchACHAccounts(dealerId) {
    try {
      this.bankAccounts = await lastValueFrom(this.paymentService.getACH(dealerId));
    } catch (err) {
      this.bankAccounts = [];
      this.isLoading = false;
    }
  }

  async fetchSEPAAccounts(dealerId) {
    try {
      this.sepaAccounts = await lastValueFrom(this.paymentService.getSEPA(dealerId));
    } catch (err) {
      this.sepaAccounts = [];
      this.isLoading = false;
    }
  }

  // ─────────────────────────────────────────────
  // Payment method selection (template events)
  // ─────────────────────────────────────────────
  onSelectPaymentMethod(data) {
    const [type, token] = data;
    this.paymentMethod = type;

    if (!token) {
      this.isValidPaymentInfo = type.includes("Others");
      this.paymentInfo = null;
      return;
    }

    this.paymentInfo = { token };
    this.isValidPaymentInfo = true;
  }

  parseNoCustomerPaymentInfo(e) {
    const [info, isValidPaymentInfo] = e;
    if (!this.paymentInfo) this.paymentInfo = {};
    if (Object.keys(info).length) {
      Object.assign(this.paymentInfo, info);
    }
    this.isValidPaymentInfo = isValidPaymentInfo;
  }

  parseCustomerPaymentInfo(payment) {
    if (!payment) return;
    if (!this.paymentInfo) this.paymentInfo = {};
    this.paymentInfo.token = payment;
    this.isValidPaymentInfo = true;
  }

  // ─────────────────────────────────────────────
  // Plan upgrade orchestration
  // ─────────────────────────────────────────────
  onClickUpgradePlan() {
    this.isLoading = true;

    switch (this.paymentMethod) {
      case "Credit or Debit card":
        if (this.cardAccounts.length && !this.paymentInfo?.type) {
          this.upgradePlanByDefaultPayment();
        } else {
          this.addCard();
        }
        break;
      case "ACH Bank Accounts":
        this.bankAccounts.length ? this.upgradePlanByDefaultPayment() : this.addAchAccount();
        break;
      case "SEPA":
        this.sepaAccounts.length ? this.upgradePlanByDefaultPayment() : this.addSEPAAccount();
        break;
      case "Others(Check By Mail, etc.)":
        this.updateDealerPlan(this.selectedPlan);
        break;
      default:
        this.paymentInfo?.cardNumber
          ? this.addCard()
          : this.errorDialog("Please close the window and try again", "Payment method is not set");
        break;
    }
  }

  upgradePlanByDefaultPayment() {
    this.setDefaultPayment(this.paymentInfo?.token);
    this.updateDealerPlan(this.selectedPlan);
  }

  setDefaultPayment(token) {
    const dealerId = this.dealerId();
    const updateData = { payment_method_id: token };
    this.paymentService.updatePaymentByStripe(dealerId, updateData).subscribe(
      () => {},
      (err) => {
        this.errorDialog(err, "Delete Payment");
        this.isLoading = false;
      },
    );
  }

  // ─────────────────────────────────────────────
  // Card add flow
  // ─────────────────────────────────────────────
  addCard() {
    this.m_warning.data = {
      header: "Add a card",
      contents: `
          <p>
            Do you want to add this card?<br/>Please confirm the card information.<br/>
          </p>
          <div><b>Card Number</b>: ${this.paymentInfo.cardNumber}</div>
          <div><b>Expired Date</b>: ${this.paymentInfo.exp}</div>
          <div><b>Nickname</b>: ${this.paymentInfo.nickname || "No nickname"}</div>
        `,
      submit_btn: "OK",
      submit_class: ["button-primary"],
      icon: "credit_card",
      color: "#2383e2",
    };
    this.m_warning.data["submit_func"] = () => {
      this.paymentService.getStripe(this.dealerId()).subscribe(
        (customers) => {
          this.hasStripeCustomerAccount = customers.length > 0;
          this.createCustomer();
        },
        () => {
          this.hasStripeCustomerAccount = false;
          this.createCustomer();
        },
      );
    };
    this.m_warning.data["cancel_func"] = () => (this.isLoading = false);
    this.m_warning.open();
  }

  createCustomer() {
    this.isLoading = true;
    const dealerId = this.dealerId();
    const data = this.buildCardPayload();

    if (!this.hasStripeCustomerAccount) {
      this.paymentService.createStripe(dealerId, data).subscribe(
        (res) => {
          this.updateDealerPlan(this.selectedPlan);
          this.setDefaultPayment(res.card_id);
        },
        (err) => {
          this.errorDialog(err, "Create Customer");
          this.isLoading = false;
        },
      );
    } else {
      this.createCard(dealerId, data);
    }
  }

  createCard(customerId, data) {
    this.paymentService.addStripe(customerId, data).subscribe(
      (res) => {
        this.setDefaultPayment(res.card_id);
        this.updateDealerPlan(this.selectedPlan);
      },
      (err) => {
        this.errorDialog(err, "Create Card");
        this.isLoading = false;
      },
    );
  }

  buildCardPayload() {
    const info = this.paymentInfo;
    const [expMonthStr, expYearStr] = info.exp.split("/");
    return {
      customer_id: this.dealerId(),
      card_number: info.cardNumber.replaceAll(" ", ""),
      exp_month: parseInt(expMonthStr),
      exp_year: parseInt(`20${expYearStr}`),
      cvc: parseInt(info.CVC),
      customer_email: this.customerEmail,
      customer_name: this.companyName,
      customer_firstname: this.firstname,
      customer_lastname: this.lastname,
      customer_country: info.country?.name ?? "United States",
      customer_address1: info.address1 ?? null,
      customer_address2: info.address2 ?? null,
      customer_city: info.city ?? null,
      customer_state: info.state ?? null,
      customer_zipcode: info.zipcode ?? null,
      metadata: { nickname: info.nickname ?? null },
    };
  }

  // ─────────────────────────────────────────────
  // ACH add flow
  // ─────────────────────────────────────────────
  addAchAccount() {
    const dealerId = this.dealerId();
    const title = `Add an ACH debit bank account`;
    const data = {
      routing_number: this.paymentInfo.routing_number,
      account_number: this.paymentInfo.account_number,
      account_holder_type: this.paymentInfo.account_holder_type,
      account_holder_name: this.paymentInfo.account_holder_name,
    };
    this.isLoading = true;
    this.paymentService
      .createACH(dealerId, data)
      .pipe(
        finalize(() => {
          this.updateDealerPlan(this.selectedPlan);
          this.isLoading = false;
        }),
      )
      .subscribe(
        (res) => {
          this.confirmDialog(title, `Successful`);
          this.setDefaultPayment(res.payment_method_id);
        },
        (err) => {
          this.errorDialog(err, title);
        },
      );
  }

  // ─────────────────────────────────────────────
  // SEPA add flow
  // ─────────────────────────────────────────────
  addSEPAAccount() {
    const dealerId = this.dealerId();
    const title = `Add SEPA account`;
    const data = {
      account_holder_name: this.paymentInfo.account_holder_name,
      account_holder_email: this.paymentInfo.account_holder_email,
      iban: this.paymentInfo.iban,
    };
    this.isLoading = true;
    this.paymentService
      .createSEPA(dealerId, data)
      .pipe(
        finalize(() => {
          this.updateDealerPlan(this.selectedPlan);
          this.isLoading = false;
        }),
      )
      .subscribe(
        (res) => {
          this.confirmDialog(title, `Successful`);
          this.setDefaultPayment(res.payment_method_id);
        },
        (err) => {
          this.errorDialog(err, title);
        },
      );
  }

  // ─────────────────────────────────────────────
  // Navigation
  // ─────────────────────────────────────────────
  goToUserPaymentPreferences() {
    this.helper.router.navigate_to("/settings/payment-preferences");
  }

  // ─────────────────────────────────────────────
  // Dialog helpers
  // ─────────────────────────────────────────────
  confirmDialog(header = "", msg = "", icon = "done", color = "green", func = null) {
    this.m_warning.data = {
      header: header,
      contents: `<p>${msg}</b></p>`,
      submit_btn: "OK",
      submit_class: ["button-primary"],
      icon: icon,
      isConfirm: true,
      color: color,
    };
    this.m_warning.data["submit_func"] = () => func;
    this.m_warning.open();
  }

  errorDialog(err, title) {
    let msg = "failed.";
    if (err.error?.message) msg = err.error.message;
    if (err._body) msg = JSON.parse(err._body).message;
    setTimeout(() => {
      this.confirmDialog(title, msg, "warning", "orange");
    }, 200);
  }
}

@Directive({
  selector: "[tooltip]",
  standalone: false,
})
export class optOutTooltipComponent {
  @Output() tooltipReadyEvent: EventEmitter<string> = new EventEmitter();

  ngOnInit() {
    this.tooltipReadyEvent.emit();
  }
}
